import React, { useState, useMemo } from "react";
import { Search, UtensilsCrossed } from "lucide-react";
import PageHeader from "../components/PageHeader";
import useReveal from "../hooks/useReveal";
import { images } from "../mock/mock";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";
import { recipes, recipeCategories } from "../data/recipes";

export default function Ricette() {
  const { t, lang } = useLang();
  const [cat, setCat] = useState("all");
  const [query, setQuery] = useState("");
  const r = t.recipes;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return recipes.filter((rec) => {
      const okCat = cat === "all" || rec.cat === cat;
      const okQ = !q || rec.name[lang].toLowerCase().includes(q) || rec.desc[lang].toLowerCase().includes(q);
      return okCat && okQ;
    });
  }, [cat, query, lang]);

  useReveal([t, cat, query]);

  return (
    <main>
      <Seo
        title="Ricette gourmet italiane | Catalogo piatti MichelChef®"
        description="Il catalogo delle ricette gourmet della tradizione italiana: primi, secondi e specialità in monoporzione, pronte da rigenerare. Anche gluten-free."
        path="/ricette"
        image={images.dish1}
      />
      <PageHeader crumb={r.crumb} eyebrow={r.eyebrow} title={r.title} subtitle={r.subtitle} image={images.dish2} homeLabel={t.common.home} />

      <section className="py-16 lg:py-24 max-w-7xl mx-auto px-5 lg:px-8">
        {/* Controls */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-5 justify-between">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCat("all")}
              className={`btn-press px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${cat === "all" ? "bg-[#17784A] text-white" : "bg-white border border-black/10 text-[#3B342E] hover:border-[#17784A]"}`}
            >
              {r.all}
            </button>
            {recipeCategories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`btn-press px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${cat === c ? "bg-[#17784A] text-white" : "bg-white border border-black/10 text-[#3B342E] hover:border-[#17784A]"}`}
              >
                {r.categories[c]}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-80">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#3B342E]/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={r.searchPh}
              className="w-full rounded-full border border-black/10 bg-white pl-11 pr-4 py-3 outline-none focus:border-[#17784A] focus:ring-2 focus:ring-[#17784A]/20 transition"
            />
          </div>
        </div>

        <p className="mt-6 text-sm text-[#3B342E]/60">
          {r.showing} {filtered.length} {r.of} {recipes.length} {r.total}
        </p>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="mt-16 text-center text-[#3B342E]/60">
            <UtensilsCrossed size={44} className="mx-auto text-[#17784A]/40" />
            <p className="mt-4 text-lg">{r.noResults}</p>
          </div>
        ) : (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((rec) => (
              <div key={rec.id} className="reveal group bg-white rounded-2xl overflow-hidden shadow-sm border border-black/5 card-lift hover:shadow-xl">
                <div className="relative h-48 overflow-hidden">
                  <img src={rec.img} alt={rec.name[lang]} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute top-3 left-3 rounded-full bg-[#14110F]/80 text-white px-3 py-1 text-xs font-semibold backdrop-blur">
                    {r.categories[rec.cat]}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold text-lg text-[#14110F] leading-snug">{rec.name[lang]}</h3>
                  <p className="mt-2 text-sm text-[#3B342E]/70 leading-relaxed">{rec.desc[lang]}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
